

import { createRoot } from 'react-dom/client';
import * as React from 'react';
import { useEffect, forwardRef, useRef, useState, useImperativeHandle} from "react";
import { FileManagerComponent, Inject, NavigationPane, DetailsView, Toolbar, Virtualization } from '@syncfusion/ej2-react-filemanager';
import { isCinematic, API_ADR, AM_UserDateTimeTZ, SetCssRoles, LoadingBar, mediaControlHeight, ProgressionBar, dBConn, AMStats} from '../../../ApiCenter/API_GET';
import { pathSeparator, foderColors} from './FileManager_support'
import './FileManager.css'
import {fileManagerExport} from './FileManager_ExcelExport'

export const FileManagerModule = forwardRef((props, ref) => {

    const fileManagerRef = useRef(null);
    const fileManagerData = useRef(props.initialData);
    const [isLoading, setIsLoading] = useState(false);

    const storeOperationElement = useRef(null);
    const isDeletingProgrammatically = useRef(false); // Flag to prevent popup during programmatic delete


    useImperativeHandle(ref, () => ({
        refreshFileManager(props, keepPath = false) {
            fileManagerRef.current.setProperties({ fileSystemData: props });
            if(!keepPath){
                fileManagerRef.current.setProperties({ path: "/" });
            }
            fileManagerRef.current.refresh();
        },
        detailsViewSwitcher(){
            switchToLargeIconsDetails()
        },
        applyOperationModal(props){

            const {operation, operationData} = props

            if(operation === "Rename"){

            }
            
            if(operation === "Delete"){
                // Set flag to prevent popup during programmatic delete
                isDeletingProgrammatically.current = true;
                
                // Delete the files
                operationData.files?.source && operationData.files?.source.forEach(el => {
                    fileManagerRef.current.deleteFiles(el.customid)
                })
                operationData.files?.target && operationData.files?.target.forEach(el => {
                    fileManagerRef.current.deleteFiles(el.customid)
                })
                
                // Reset flag after deletion
                setTimeout(() => {
                    isDeletingProgrammatically.current = false;
                }, 100);
                
                // Delete the folders (in reverse order to erase the deepest first)
                if(operationData.folders && operationData.folders.length > 0){
                    operationData.folders.reverse().forEach(folderId => {
                        fileManagerRef.current.deleteFiles(folderId)
                    })
                }
            }
        },
        setFilteredData(filteredResults, searchValue){
            // Build a minimal tree structure with filtered results
            // Include root and necessary parent folders for navigation
            const filteredData = [];
            const addedIds = new Set();
            
            // Add root
            const root = fileManagerData.current.find(el => el.id === '0');
            if(root){
                filteredData.push(root);
                addedIds.add('0');
            }
            
            // For each result, add it and all its parents
            filteredResults.forEach(item => {
                // Add all parents up to root
                let currentId = item.parentId;
                const parentsToAdd = [];
                
                while(currentId && currentId !== '0' && !addedIds.has(currentId)){
                    const parent = fileManagerData.current.find(el => el.id === currentId);
                    if(parent){
                        parentsToAdd.unshift(parent);
                        currentId = parent.parentId;
                    } else {
                        break;
                    }
                }
                
                // Add parents
                parentsToAdd.forEach(p => {
                    filteredData.push(p);
                    addedIds.add(p.id);
                });
                
                // Add the item itself
                if(!addedIds.has(item.id)){
                    filteredData.push(item);
                    addedIds.add(item.id);
                }
            });
            
            console.log('Setting filtered data:', filteredData.length, 'items');
            
            // Replace the fileSystemData
            fileManagerRef.current.fileSystemData = filteredData;
            fileManagerRef.current.path = '/';
            fileManagerRef.current.refresh();
        },
        restoreOriginalData(){
            console.log('Restoring original data');
            fileManagerRef.current.fileSystemData = fileManagerData.current;
            fileManagerRef.current.path = '/';
            fileManagerRef.current.refresh();
        },
        goto({ item, searchValue }){
            console.log('Navigating to item:', item);
            
            // Build the path by traversing up the parent chain using IDs
            const buildPathFromParents = (itemId) => {
                const pathParts = [];
                let currentId = itemId;
                
                while(currentId && currentId !== '0') {
                    const currentItem = fileManagerData.current.find(el => el.id === currentId);
                    if(!currentItem) break;
                    
                    pathParts.unshift(currentItem.id);
                    currentId = currentItem.parentId;
                }
                
                return '/' + pathParts.join('/');
            };
            
            // Build path to the parent folder
            const targetPath = buildPathFromParents(item.parentId);
            
            console.log('Navigating to:', targetPath);
            
            // Navigate to the parent folder
            fileManagerRef.current.path = targetPath;
            
            // Select the item after navigation
            setTimeout(() => {
                try {
                    const fileName = item.name.includes('/') ? item.name.split('/').pop() : item.name;
                    fileManagerRef.current.clearSelection();
                    fileManagerRef.current.setProperties({ selectedItems: [fileName] });
                    console.log('Selected:', fileName);
                } catch(err){
                    console.error('Error selecting item:', err);
                }
            }, 300);
        }
    }))


    const pathSeparatorTemplate = (props) => {
        return <div style={{display: "inline"}}>{props.isFile ? pathSeparator(props.name) : props.name}</div>

    }

    const uploaderTemplate = (props) => {
        return <div>{props.uploader ? props.uploader : ''}</div>
    }

    const dateTemplate = (props) => {
        return props?.dateVisible ? <div>{props.dateVisible}</div> : null
    }

    const FileTypeTemplate = (props) => {     
        return props?.customData?.file_description ? <div>{props.customData.file_description}</div> : null
    }

    const transStatusTemplate = (props) => {
        if(props.type === 'folder') {
            if(props.pendingTr === -1) return null;
            const transPercent = Math.floor(1000 * (props.totalFiles - props.pendingTr) / props.totalFiles) / 10;
            return transPercent + '%';
        }
        const status = props?.transcription_status === 1 && !props?.transcription_text ? 2 : props?.transcription_status === null ? -1 : props?.transcription_status;
        const options = {
            class: `bi bi-${status === 1 ? 'check-circle' : status === 0 ? 'arrow-repeat' : 'slash-circle'}`,
            color: status === 1 ? '#00a100' : status === 0 ? '#0099ff' : '#b00',
            title: status === 1 ? 'Transcription completed' : status === 0 ? 'Transcription pending' : 'Transcription not available',
        }
        if(status === -1) return null;
        return (
            <div 
                style={{fontSize: '20px', cursor: 'help', color: options.color}}
                className={options.class}
                title={options.title}
            />
        )
    }

    const onFileOpen = async (args) => {

        try{

            if(args.fileDetails.isFile === true){
                const element = {
                    s3filename: `project-${args.fileDetails.customData.storage_folder_name}${args.fileDetails.customData.id}${args.fileDetails.customData.file_extension}`,
                    type: args.fileDetails.customData.file_description,
                    realFilename: args.fileDetails.customData.original_fullname,
                    fromManualPlay: true,
                }

                if(!isCinematic(element.s3filename)){
                    element.s3filename = `projects-audiomate/${element.s3filename}`
                    props.parsePlayAudio(element)

                } else {
                    props.playVideo({url: element.s3filename, filename: element.realFilename})
                }    
            }


            if (args.fileDetails.isFile === false) {

/*
                const folderId = args.fileDetails.id

                //console.log("CHECK FOLDER", folderId)
                
                // Carica i dati solo se la cartella non ha già contenuti caricati
                const hasContent = fileManagerData.current.find(item => item.parentId === folderId)
                console.log("THIS", folderId, hasContent)
                if (!hasContent) {
                    console.log("Caricamento contenuti per folder:", folderId)
                    setIsLoading(true)
                    await updateData(folderId)
                    setIsLoading(false)
                }*/
            }
        }
        catch(e){
        
        }
    }
    
    const switchToLargeIconsDetails = () => {
        if (fileManagerRef.current){
            //console.log(fileManagerRef.current.view)
            fileManagerRef.current.view = fileManagerRef.current.view === 'Details' ? "LargeIcons" : "Details";
        }
    };
    // initial data
    const onFileLoad = (args) => {

        // Customise the folder icon color (Normal, UserSource, UserTarget)
        if (!args.fileDetails.isFile && args.fileDetails.size !== 0) {

          const icon = args.element.querySelector('.e-fe-folder');
          if (icon) {

          
            let color;
            if (args.fileDetails?.im === 'Tag') {
              color = foderColors['Tag'];
            } else if (args.fileDetails?.im === 'Package') {
              color = foderColors['Package']; // o un colore specifico per Package se vuoi
            } else if (args.fileDetails?.uploadedStructure) {
              color = foderColors[`User${args.fileDetails?.sourceTarget}`];
            } else {
              color = foderColors['Normal'];
            }
            
            const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'>
              <path fill='${color}' d='M29.5,7.14h-13L13.06,4H2.5A2.5,2.5,0,0,0,0,6.5v19A2.5,2.5,0,0,0,2.5,28h27A2.5,2.5,0,0,0,32,25.5V9.64A2.5,2.5,0,0,0,29.5,7.14Z'/>
            </svg>`;
            const encoded = encodeURIComponent(svg);
            icon.style.backgroundImage = `url("data:image/svg+xml,${encoded}")`;
          }
        }
        
        // Add lock icon for non-editable items
        if (args.fileDetails.permRename === false && args.fileDetails.permDelete === false) {
          // Try multiple selectors for different views
          let nameElement = args.element.querySelector('.e-list-text') || 
                           args.element.querySelector('.e-fe-text') ||
                           args.element.querySelector('.e-list-item-name');
          
          // For Details view (table row), find the name cell
          if (!nameElement && args.element.classList.contains('e-row')) {
            const nameCell = args.element.querySelector('.e-fe-grid-name');
            if (nameCell) {
              nameElement = nameCell;
            }
          }
          
          // Debug: log if we can't find the element
          if (!nameElement) {
                console.log('Lock icon - Could not find name element for:', args.fileDetails.name, 'Element classes:', args.element.className);
          }
          
          if (nameElement && !nameElement.querySelector('.lock-icon')) {
            const lockIcon = document.createElement('span');
            lockIcon.className = 'lock-icon';
            lockIcon.innerHTML = '🔒 ';
            lockIcon.style.display = 'inline';
            lockIcon.style.marginRight = '5px';
            lockIcon.style.fontSize = '14px';
            lockIcon.title = 'Protected item - Cannot be modified';
            nameElement.insertBefore(lockIcon, nameElement.firstChild);
          }
        }
    };

    const onBeforePopup = (args) => {

        const forbidden = [];
        if(args.fileDetails[0].permRename === false){
            forbidden.push("Rename");
        }

        if(args.fileDetails[0].permDelete === false){
            forbidden.push("Delete");
        }

        args.items = args.items.filter(item => !forbidden.includes(item.text));
  
    };

    const onBeforePopupOpen = (args) => {

        if(['Rename', 'Delete'].includes(args.popupName)){
            args.cancel = true 

            // If deleting programmatically, just cancel and return
            if(isDeletingProgrammatically.current){
                return;
            }
          
            const selectedData = fileManagerRef.current.getSelectedFiles();
           
            if(!selectedData.length){
                return 
            } 
      
            if(args.popupName === 'Rename' && selectedData.length > 1){
                //console.log("ONLY ONE FILE IS ALLOWED")
                return
            }

            // Checks if at least one of the selected items lacks the required permission
            const lacksPermission = args.popupName === 'Rename' 
                ? selectedData.some(item => item.permRename === false)
                : selectedData.some(item => item.permDelete === false);
            
            if(lacksPermission){
                const nonPermittedItems = args.popupName === 'Rename' 
                    ? selectedData.filter(item => item.permRename === false)
                    : selectedData.filter(item => item.permDelete === false);
                const itemNames = nonPermittedItems.map(item => `"${item.name}"`).join(', ');
                const operation = args.popupName === 'Rename' ? 'rename' : 'delete';
                
                //alert(`Cannot ${operation} the following protected items:\n${itemNames}\n\nOnly user-created folders, packages (SPK), source files (SF), and tags (TTAG) can be modified.`);
                //console.log(`CANNOT ${operation.toUpperCase()} ITEMS WITHOUT PERMISSION`/*, nonPermittedItems*/);
                return
            }

            if(selectedData){
                const object = {
                    operation: args.popupName,
                    data: selectedData
                }
                props.modalOperation(object)
            }
        }
          
    }

    const onBeforeDelete = (args) => {
        //args.cancel = true
    }

    const onFileSelection = (args) => {

        if(args.fileDetails?.isFile === true && args.action === "select"){
            // check if the player is enabled
            const el = document.querySelector('#playerContainer .btn.btn-sm.autoplay.active');
            if (el) {
                onFileOpen(args)
            }   
        }
        //storeOperationElement.current = args.fileDetails 
    }

    const onMenuClick = async (args) => {

        if(args.item.properties.text === "Export"){

            props.spinner(true)

            setTimeout(async () => {

                const selectedFolderId = args.fileDetails[0].id
                
                const getAllFilesInFolder = (folderId) => {
                    const files = []
                    
                    fileManagerData.current.forEach(item => {
                        if(item.parentId === folderId){
                            if(item.isFile){
                                files.push(item)
                            } else {
                                files.push(...getAllFilesInFolder(item.id))
                            }
                        }
                    })                   
                    return files
                } 
                const list = getAllFilesInFolder(selectedFolderId)
                await fileManagerExport(list)
                props.spinner(false)
            })

        }
        
    }



    
    return (<div style={{position: 'relative', width: '100%', height: '100%'}}>
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    fontSize: '18px',
                    fontWeight: 'bold'
                }}>
                    Loading...
                </div>
            )}
            <div className="control-section" style={{position: 'relative', height: '100%'}}>
                {<FileManagerComponent 
                    ref={fileManagerRef}
                    id="AudioMateAssets" 
                    view="Details"
                    height={"calc(100vh - 280px)"}
                    fileSystemData={fileManagerData.current} 
                    toolbarSettings={
                       // {/*items: ['NewFolder', 'Cut', 'Copy', 'Paste', 'Delete', 'Rename', 'SortBy', 'Refresh', 'Selection', 'View', 'Details'] */}
                        {items: [] }
                    }
                    contextMenuSettings={
                        {file: ["Open", "|", /*"Cut", "Copy", "Paste",*/ "Delete", "Rename", /*"|", "Details"*/], folder: ["Open", "|",/* "Cut", "Copy", "Paste", "|",*/ "Delete", "Rename", "|", "Export"], layout: ["SortBy", "View", "Refresh", "|", "Paste", "|", "NewFolder", "|", "Details", "|", "SelectAll"], visible: true}
                        //{file: []}
                    }
                    detailsViewSettings={{
                        columns: [
                            { field: 'name', headerText: 'Filename', minWidth: 120, width: '120', customAttributes: { class: 'e-fe-grid-name' }, template: pathSeparatorTemplate },
                            { field: 'uploader', headerText: 'Uploader', minWidth: 50, width: '110', template: uploaderTemplate, textAlign: 'Center' },
                            { field: '_fm_modified', headerText: 'Date Modified', minWidth: 50, width: '190', template: dateTemplate, textAlign: 'Center' },
                            { field: 'FileType', headerText: 'FileType', minWidth: 50, width: '110', template: FileTypeTemplate },
                            { field: 'size', headerText: 'File Size', minWidth: 50, width: '110', textAlign: 'Center' },
                            { field: 'totalFiles', headerText: 'File Count', minWidth: 50, width: '110', textAlign: 'Center' },
                            { field: 'transcription_text', headerText: 'Transcription Text', minWidth: 50, width: 300, textAlign: 'Left' },
                            { field: 'transcription_status', headerText: 'Status', minWidth: 80, width: 80, textAlign: 'center', template: transStatusTemplate },
                        ]
                    }}
                    enableVirtualization={true}
                    fileOpen={onFileOpen}
                    fileLoad={onFileLoad}
                    menuOpen={onBeforePopup}
                    beforePopupOpen={onBeforePopupOpen}
                    beforeDelete={onBeforeDelete}
                    fileSelection={onFileSelection}
                    menuClick={onMenuClick}
                    >
                    <Inject services={[NavigationPane, DetailsView, Virtualization]}/>
                </FileManagerComponent>}
            </div>

        </div>);
})
